import { useMemo } from "react";

import { filterTruthy, SortDirection } from "@bridgesplit/utils";

import { useTimelockListQuery } from "../reducers";
import { ParsedTimeLock, ParsedTimeLockUpdateParams, TimeLockFilter, TimeLockUpdateType } from "../types";
import { useAbfTypesToUiConverter } from "../utils";

export function useTimelocks({ filter, skip: skipParam }: { filter: Partial<TimeLockFilter>; skip?: boolean }) {
    const rawFilter = useMemo((): TimeLockFilter => {
        return {
            vaultAddresses: filter.vaultAddresses ?? [],
            timelockStatus: filter.timelockStatus ?? undefined,
            page: filter.page ?? 0,
            pageSize: filter.pageSize ?? 20,
            sortDirection: filter.sortDirection ?? SortDirection.Desc,
            includeInstant: filter.includeInstant ?? false,
            ...filter
        };
    }, [filter]);

    const {
        data: timelockData,
        isLoading,
        error
    } = useTimelockListQuery(rawFilter, {
        skip: skipParam
    });

    const { convertTimelock: convertTimeLock } = useAbfTypesToUiConverter();

    const convertedTimelocks = useMemo(() => {
        if (isLoading || !timelockData?.timeLocks) return undefined;

        const timelocks = timelockData.timeLocks.map((timeLock: ParsedTimeLock) => {
            if (!timeLock.principalMint) return undefined;
            return convertTimeLock(timeLock);
        });

        return timelocks.filter(filterTruthy);
    }, [timelockData?.timeLocks, isLoading, convertTimeLock]);

    const totalTimelocks = timelockData?.total != null ? Number(timelockData.total) : undefined;

    return { timelocks: convertedTimelocks, totalTimelocks, isLoading, error };
}

/**true
 * Type guard: Checks if a timelock update is of a specific type.
 */
export function isTimelockUpdateType<T extends TimeLockUpdateType>(
    update: ParsedTimeLockUpdateParams,
    type: T
): update is Extract<ParsedTimeLockUpdateParams, { type: T }> {
    return update.type === type;
}
